import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, Trash2 } from "lucide-react";
import { Flash } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const FlashNotesSheet = ({ form, onOpenChange, onChanged }) => {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (form) Flash.comments(form.id).then(setNotes);
    setText("");
  }, [form]);

  const add = async () => {
    setBusy(true);
    try {
      const n = await Flash.addComment(form.id, text);
      setNotes((x) => [n, ...x]);
      setText("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || "Could not save note");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await Flash.removeComment(id);
      setNotes((x) => x.filter((n) => n.id !== id));
      onChanged();
    } catch {
      toast.error("Could not delete note");
    }
  };

  return (
    <Sheet open={!!form} onOpenChange={onOpenChange}>
      <SheetContent className="bg-white w-full sm:max-w-md flex flex-col" data-testid="flash-notes-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading">{form?.name}</SheetTitle>
          <SheetDescription className="flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Owner notes · stay in the app, never printed on the PDF
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-2 mt-4">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={`Note for ${form?.owner || "the owner"}…`} data-testid="flash-note-input" />
          <Button onClick={add} disabled={busy || !text.trim()} className="w-full bg-[#0284c7] hover:bg-[#0369a1]" data-testid="flash-note-add">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Add note
          </Button>
        </div>
        <div className="mt-5 flex-1 overflow-y-auto space-y-3" data-testid="flash-notes-list">
          {notes.length === 0 && <p className="text-sm text-slate-400">No notes yet.</p>}
          {notes.map((n) => (
            <div key={n.id} className="group rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`flash-note-${n.id}`}>
              <div className="flex items-center justify-between text-[11px] text-slate-500">
                <span className="font-semibold text-slate-700">{n.author || "Unknown"}</span>
                <span className="flex items-center gap-2">
                  {when(n.created_at)}
                  <button onClick={() => remove(n.id)} className="opacity-40 hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100 text-rose-600 transition-opacity" data-testid={`flash-note-delete-${n.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </span>
              </div>
              <p className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{n.text}</p>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};
